<script>
import api from '@utils/api'
import { isNil } from 'lodash'
import { getDocumentTypeId } from '@utils/transformations'
import * as compare from '@utils/comparers'

export default {
  name: 'NewDocument',
  props: {
    newDocument: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      valid: true,
      fromOrganizationsLoading: false,
      fromOrganizations: [],
      fromWarehouse: [],
      fromWarehouseLoading: false,
      toWarehouse: [],
      toWarehouseLoading: false,
      item: {
        type: getDocumentTypeId(this.$route.params.documentType),
        from: null,
        fromWarehouse: null,
        to: null,
        toWarehouse: null,
      },
      rules: {
        type: [x => !!x || this.$t('validation__is_required')],
        from: [x => !!x || this.$t('validation__is_required')],
        fromWarehouse: [x => !!x || this.$t('validation__is_required')],
        to: [x => !!x || this.$t('validation__is_required')],
        toWarehouse: [x => !!x || this.$t('validation__is_required')],
      },
      loading: false,
    }
  },
  computed: {
    documentType() {
      return this.item.type
    },
    defaultOrganization() {
      return this.$store.state.data.myOrganization
    },
    isMerge() {
      return this.item.type === 4
    },
    type() {
      return this.$store.getters['data/getDictionary']('documenttype').map(item=>{
        item.translatedName = this.$t(`documenttype.${item.code}.name`);
        return item
      })
    },
    from() {
      const orgs = this.$store.getters['data/getOrganization']
      switch (this.documentType) {
        case 2:
        case 4:
          return orgs.filter(elem => elem.id === this.defaultOrganization)
        default:
          return orgs
      }
    },
    to() {
      const orgs = this.$store.getters['data/getOrganization']
      switch (this.documentType) {
        case 1:
          return orgs.filter(elem => elem.id === this.defaultOrganization)
        case 2:
          return orgs.filter(elem => elem.id !== this.defaultOrganization)
        default:
          return orgs
      }
    },
    currentFrom() {
      return this.item.from
    },
    currentFromWarehouse() {
      return this.item.fromWarehouse
    },
    currentTo() {
      return this.item.to
    },
  },
  watch: {
    currentFrom(value) {
      if (this.isMerge) this.item.to = value
      if (isNil(value)) return
      this.fromWarehouseLoading = true
      this.item.fromWarehouse = null
      api.getOrganizationChild(value).then(response => {
        this.fromWarehouse = response.sort(compare.byShortName)
        if (response.length === 1) this.item.fromWarehouse = response[0].id
        else {
          const organization = this.$store.getters['data/getOrganization'].find(
            elem => elem.id === value
          )
          const mainWarehouseId = organization.mainWarehouse || null
          if (mainWarehouseId) {
            const main = response.find(elem => elem.id === mainWarehouseId)
            if (main) this.item.fromWarehouse = main.id
          }
        }
        this.fromWarehouseLoading = false
      })
    },
    currentFromWarehouse(value) {
      if (this.isMerge) this.item.toWarehouse = value
    },
    currentTo(value) {
      if (isNil(value)) return
      this.toWarehouseLoading = true
      this.item.toWarehouse = null
      api.getOrganizationChild(value).then(response => {
        this.toWarehouse = response.sort(compare.byShortName)
        if (response.length === 1) this.item.toWarehouse = response[0].id
        else {
          const organization = this.$store.getters['data/getOrganization'].find(
            elem => elem.id === value
          )
          const mainWarehouseId = organization.mainWarehouse || null
          if (mainWarehouseId) {
            const main = response.find(elem => elem.id === mainWarehouseId)
            if (main) this.item.toWarehouse = main.id
          }
        }
        this.toWarehouseLoading = false
      })
    },
    newDocument(value) {
      if (value) {
        const myOrgId = this.$store.state.data.myOrganization
        switch (getDocumentTypeId(this.$route.params.documentType)) {
          case 1: // accept
          case 6: // returns
            this.item.to = myOrgId
            break
          case 2: // completeSet
            this.item.from = myOrgId
            break
          case 4: // shipment
          case 5: // writeOff
            this.getOrganizations()
            this.item.from = myOrgId
            break
          default:
            break
        }
      }
    },
  },
  methods: {
    closeItem() {
      this.$emit('close-modal')
      this.$refs.newDocumentForm.reset()
      this.item.type = getDocumentTypeId(this.$route.params.documentType)
    },
    submit() {
      if (this.$refs.newDocumentForm.validate()) {
        this.loading = true
        api.createDocument(this.item).then(response => {
          if (response) {
            this.$router.push(
              `${this.$route.params.documentType}/${response.id}`
            )
          }
          this.loading = false
        })
      }
    },
    getOrganizations() {
      const myOrganizationId = this.$store.state.data.myOrganization
      const myOrganizations = this.$store.getters['data/getOrganization'].find(
        elem => elem.id === myOrganizationId
      )
      this.item.to = myOrganizationId
      this.fromOrganizations.push(myOrganizations)
    },
  },
}
</script>

<template>
  <v-dialog
    v-model="newDocument"
    persistent
    max-width="500px">
    <v-card>
      <v-card-title>
        <v-spacer />
        <div class="headline">{{ $t('titles__new_document') }}</div>
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex
              xs12
              class="text-xs-center">
              <v-form
                ref="newDocumentForm"
                v-model="valid"
                lazy-validation>
                <v-select
                  :items="type"
                  :label="$t('label__type')"
                  v-model="item.type"
                  :rules="rules.type"
                  item-text="translatedName"
                  item-value="code"
                  required
                  autocomplete
                  readonly
                />
                <v-select
                  :items="this.$route.params.documentType === 'writeOff' ? fromOrganizations : from"
                  :label="$t('label__from')"
                  v-model="item.from"
                  :rules="rules.from"
                  :loading="fromOrganizationsLoading"
                  item-text="shortName"
                  item-value="id"
                  required
                  autocomplete />
                <v-select
                  :items="fromWarehouse"
                  :label="$t('label__fromWarehouse')"
                  :disabled="!item.from"
                  :rules="rules.fromWarehouse"
                  :loading="fromWarehouseLoading"
                  v-model="item.fromWarehouse"
                  item-text="shortName"
                  item-value="id"
                  required
                  autocomplete />
                <v-select
                  v-if="item.type !== 5"
                  :items="to"
                  :label="$t('label__to')"
                  v-model="item.to"
                  :rules="rules.to"
                  :readonly="isMerge"
                  item-text="shortName"
                  item-value="id"
                  required
                  autocomplete />
                <v-select
                  v-if="item.type !== 5"
                  :items="toWarehouse"
                  :label="$t('label__toWarehouse')"
                  :disabled="isMerge ? false : !item.to"
                  :rules="rules.toWarehouse"
                  :loading="toWarehouseLoading"
                  :readonly="isMerge"
                  v-model="item.toWarehouse"
                  item-text="shortName"
                  item-value="id"
                  required
                  autocomplete />
              </v-form>
            </v-flex>
            <small>* {{ $t('label__requier_small_text') }}</small>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          :disabled="loading"
          color="secondary"
          @click="closeItem">{{ $t('buttons__close') }}</v-btn>
        <v-btn
          :disabled="loading"
          :loading="loading"
          color="primary"
          @click="submit">{{ $t('buttons__create') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" module>
@import '@design';
</style>
